<template>
  <div :data-app-id="app.appID" :data-app-name="app.name" class="build-app" :class="{ 'build-app--dark': isDarkMode }">
    <h2 class="ml-2 mt-2" v-if="isGlobalView">
      {{ app.org_name }}
    </h2>
    <div class="build-app__header">
      <div class="build-app__header--title">
        <div class="d-flex align-center">
          <portal-target class="build-app__status-led" :name="app.appID">
          </portal-target>
          <Help article="build/app/name" />
          <v-text-field
            dense
            id="app-name"
            v-model="app.real_name"
            title="Change app name"
            :readonly="!renameAppStatus"
            @keydown.enter="renameApp"
            @dblclick.native="toggleRenameApp"
            hide-details="auto"
            class="build-app__app-name h2"
          />
          <div class="content-editable__actions">
            <apio-icon
              v-if="!renameAppStatus"
              class="build-app__pointer"
              name="ui-edit"
              title="Edit Name"
              @click.native="toggleRenameApp"
            >
            </apio-icon>
            <div class="build-app__save-cancel-name-edit">
              <apio-icon
                v-if="renameAppStatus"
                class="build-app__pointer mr-2"
                name="ui-save"
                title="Save name"
                @click.native="renameApp"
              >
              </apio-icon>
              <apio-icon
                v-if="renameAppStatus"
                name="ui-circle-cross-line"
                class="build-app__pointer"
                title="Cancel editing"
                @click.native="toggleRenameApp"
              >
              </apio-icon>
            </div>
          </div>
        </div>
      </div>
      <div class="build-app__header-actions">
        <apio-switch
          :value="alert"
          :color="'green'"
          @change="toggleAlert"
          ><template slot="left-label"
            ><span class="small-extra-bold">Alerts</span></template
          ></apio-switch
        >
        <apio-button
          class="mr-2"
          button-text-size="tiny"
          button-size="small"
          button-type="primary"
          button-icon=""
          @click="alertsDialog = true"
          ><span class="mr-2" v-if="$vuetify.breakpoint.lg || $vuetify.breakpoint.xl">ALERTING</span>
          <apio-icon
            name="ui-cloud-alert"
            lightModeColour="white"
            darkModeColour="white"
          />
        </apio-button>
        <apio-button
          v-if="$vuetify.breakpoint.lg || $vuetify.breakpoint.xl"
          class="mr-2"
          button-text-size="tiny"
          button-size="small"
          button-type="primary"
          button-icon=""
          @click="
            $router.push(
              '/organisation-dashboard?c=' + $router.history.current.query.c
            )
          "
          >DASHBOARD
          <apio-icon
            class="ml-1"
            name="nav-cog"
            lightModeColour="white"
            darkModeColour="white"
          />
        </apio-button>
        <portal-target :name="runInCloudPortal" />
        <portal-target :name="upgradeButtonPortal" />
        <portal-target :name="buildButtonPortal" />
        <apio-button
          class="build-app__header-actions--item mr-2"
          button-text-size="tiny"
          button-size="small"
          button-type="primary"
          button-icon=""
          @click.native="$root.$emit('edit-env', app)"
        >
          ENVIRONMENT
          <apio-icon
            class="ml-1"
            name="ui-environment"
            lightModeColour="white"
            darkModeColour="white"
          />
        </apio-button>
        <apio-icon
          @click.native="copyApp"
          @mouseover.native="onSecondaryButtonMouseOver('copy')"
          @mouseout.native="onSecondaryButtonMouseOut('copy')"
          v-if="$vuetify.breakpoint.xs"
          :lightModeColour="secondaryButtonIconColours.copy.color"
          :darkModeColour="secondaryButtonIconColours.copy.color"
          class="build-app__header-actions--item ml-1"
          name="ui-copy"
        />
        <apio-button
          v-if="!$vuetify.breakpoint.xs"
          class="build-app__header-actions--item mr-2"
          button-text-size="tiny"
          button-size="small"
          button-type="secondary"
          @mouseover.native="onSecondaryButtonMouseOver('copy')"
          @mouseout.native="onSecondaryButtonMouseOut('copy')"
          @click="copyApp"
        >
          <apio-icon
            @mouseover.native="onSecondaryButtonMouseOver('copy')"
            @mouseout.native="onSecondaryButtonMouseOut('copy')"
            :lightModeColour="secondaryButtonIconColours.copy.color"
            :darkModeColour="secondaryButtonIconColours.copy.color"
            name="ui-copy"
            @click="copyApp"
        /></apio-button>
        <apio-button
          v-if="!$vuetify.breakpoint.xs"
          class="build-app__header-actions--item"
          button-text-size="tiny"
          button-size="small"
          button-type="secondary"
          @mouseover.native="onSecondaryButtonMouseOver('delete')"
          @mouseout.native="onSecondaryButtonMouseOut('delete')"
          @click="deleteApp"
        >
          <apio-icon
            :lightModeColour="secondaryButtonIconColours.delete.color"
            :darkModeColour="secondaryButtonIconColours.delete.color"
            @mouseover.native="onSecondaryButtonMouseOver('delete')"
            @mouseout.native="onSecondaryButtonMouseOut('delete')"
            name="ui-trash"
        /></apio-button>
        <apio-icon
          :lightModeColour="secondaryButtonIconColours.delete.color"
          :darkModeColour="secondaryButtonIconColours.delete.color"
          @mouseover.native="onSecondaryButtonMouseOver('delete')"
          @mouseout.native="onSecondaryButtonMouseOut('delete')"
          v-if="$vuetify.breakpoint.xs"
          class="build-app__header-actions--item ml-1"
          name="ui-trash"
          @click.native="deleteApp"
        />
        <Help article="build/app/actions" />
      </div>
    </div>

    <BuildList :clientHash="clientHash" :app="app" />
    <v-dialog max-width="600" v-model="alertsDialog">
      <v-card>
        <v-toolbar flat color="apioshade3" class="white--text h2 mb-6">
          <div class="white--text run-step__title">Alerting configuration</div>
          <v-spacer></v-spacer>
          <apio-button
            button-type="get-started"
            button-size="small"
            @click.native="alertsDialog = false"
            >Close
            <apio-icon
              class="ml-2"
              size="16"
              name="cross"
              lightModeColour="white"
              darkModeColour="white"
          /></apio-button>
        </v-toolbar>
        <div class="px-4" id="loadscreen" hide-footer title="Alerts">
          <v-card flat class="editor-inner__col">

            <div class="small-extra-bold mb-2">Alert On...</div>

            <div>
              <v-switch class="float-left mr-2" default="true" v-model="alert_config.app_went_down"/>
              <p class="float-left">The App has quit or disconnected</p>
              <div class="w-break"></div>
            </div>

            <div>
              <v-switch class="float-left mr-2" :value="(alert_config.last_data_time != '')"/>
              <p class="float-left">App not sending anything to dashboard for</p>
              <v-text-field class="w-small float-left" dense :value="alert_config.last_data_time"></v-text-field>
              <p class="float-left">minutes</p>
              <div class="w-break"></div>
            </div>

            <div>
              <v-switch class="float-left mr-2" :value="(alert_config.payload_stuck_minutes != '')"/>
              <p class="float-left">Payload is stuck in any step for</p>
              <v-text-field class="w-small float-left" dense :value="alert_config.payload_stuck_minutes"></v-text-field>
              <p class="float-left">minutes</p>
              <div class="w-break"></div>
            </div>

            <div class="small-extra-bold">Alert type</div>

            <v-select
              v-model="alert_config.alert"
              dense
              outlined
              class="flex-grow-1 mb-4"
              :items="['', 'email', 'slack', 'both']"
              hide-details="auto"
            >
            </v-select>

            <div
              v-if="alert_config.alert == 'email' || alert_config.alert == 'both'"
              class="small-extra-bold">Email address for alerting</div>
            <v-text-field
              v-model="alert_config.email"
              class="mb-4"
              v-if="alert_config.alert == 'email' || alert_config.alert == 'both'"
              outlined
              dense
              hide-details="auto"
              placeholder="Email"
            />
            <div
              v-if="alert_config.alert == 'slack' || alert_config.alert == 'both'"
              class="small-extra-bold">Slack Channel</div>
            <v-text-field
              v-model="alert_config.slack_channel"
              class="mb-4"
              v-if="alert_config.alert == 'slack' || alert_config.alert == 'both'"
              outlined
              dense
              hide-details="auto"
              placeholder="Slack Channel"
            />
            <div
              v-if="alert_config.alert == 'slack' || alert_config.alert == 'both'"
              class="small-extra-bold">Slack API Key</div>
            <v-text-field
              v-model="alert_config.slack_apikey"
              v-if="alert_config.alert == 'slack' || alert_config.alert == 'both'"
              outlined
              dense
              hide-details="auto"
              placeholder="Slack API Key"
            />
          </v-card>
        </div>
        <v-card-actions>
          <v-spacer />
          <apio-button color="success" class="mr-2" button-size="medium" @click.native="saveAlertingConfig"
            >Save
          </apio-button>
          <apio-button
            button-type="secondary"
            button-size="medium"
            @click.native="alertsDialog = false"
            >Cancel</apio-button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <apio-snackbar
      :text="snackBarText"
      :status="snackBarStatus"
      :snackbar="snackbar"
      @close="snackbar = false"
    />
  </div>
</template>

<script>

import editor from '@/API/services/editor';

const BuildList = () => import("@/components/BuildList/BuildList.vue");

export default {
  name: "BuildApp",
  components: {
    BuildList,
    Help: () => import("@/components/Help/Help.vue"),
    ApioSwitch: () => import("@/components/Misc/Switch.vue"),
  },
  props: {
    clientHash: {
      type: [String, Number],
      required: true,
    },
    app: {
      type: Object,
      required: true,
    },
  },
  async mounted() {
    await this.getAlertConfiguration();
  },
  data: () => ({
    builds: [
      { title: "Linux", params: "&s=linux" },
      { title: "Docker Container", params: "&s=linux&t=true" },
      { title: "macos (x86)", params: "&s=darwin" },
      { title: "Windows", params: "&s=windows" },
    ],
    snackbar: false,
    snackBarStatus: "",
    snackBarText: "",
    alert_config: {
    },
    alert: false,
    alertsDialog: false,
    alert2: false,
    newAppName: "",
    currentApp: "",
    editStatus: false,
    backupName: "",
    localAppName: "",
    secondaryButtonIconColours: {
      copy: {
        color: "cyan",
        light: {
          normal: "cyan",
          hover: "black",
        },
        dark: {
          normal: "cyan",
          hover: "cyan",
        },
      },
      delete: {
        color: "cyan",
        light: {
          normal: "cyan",
          hover: "black",
        },
        dark: {
          normal: "cyan",
          hover: "cyan",
        },
      },
    },
    renameAppStatus: false,
  }),
  methods: {
    toggleAlert() {
      this.alert = !this.alert;
      this.saveAlertingConfig();
    },
    async saveAlertingConfig() {
      try {
        const payload = {
          alert: this.alert === true ? 1 : 0,
          appHash: this.app.appID,
          clientHash: this.clientHash,
          alert_config: JSON.stringify(this.alert_config),
        }
        await editor.setAppAlertingConfig(payload);
        this.alertsDialog = false;
        this.snackbar = true;
        this.snackBarText = 'Alerts configuration saved';
        this.snackBarStatus = 'success'
      } catch(err) {
        console.log(err);
      }
    },
    async getAlertConfiguration() {
      try {
        const payload = {
          appHash: this.app.appID,
          clientHash: this.clientHash,
        }
        if(this.app.appID && this.clientHash) {
          const { data } = await editor.getAppAlertingConfig(payload);
          if(data !== {}) {
            var ac = JSON.parse(data.alert_config);
            if (ac.app_went_down == undefined) {
              ac.app_went_down = true
            }
            if (ac.payload_stuck_minutes == undefined) {
              ac.payload_stuck_minutes = 5
            }
            this.alert_config = ac
            this.alert === 1 ? true : false;
          }
        }
      } catch(err) {
        console.log('Could not retrieve alert configuration');
      }
    },
    toggleRenameApp() {
      this.renameAppStatus = !this.renameAppStatus;
      if(this.renameAppStatus) {
        document.getElementById("app-name").focus();
      }
    },
    onSecondaryButtonMouseOver(index) {
      return (this.secondaryButtonIconColours[index].color =
        this.secondaryButtonIconColours[index][this.theme].hover);
    },
    onSecondaryButtonMouseOut(index) {
      return (this.secondaryButtonIconColours[index].color =
        this.secondaryButtonIconColours[index][this.theme].normal);
    },
    renameApp() {
      this.$emit("renameApp", {
        app: this.app,
        appName: this.app.name,
        clientHash: this.clientHash,
        newAppName: this.app.real_name,
      });
      this.toggleRenameApp();
    },
    deleteApp() {
      this.$emit("deleteApp", this.app);
    },
    copyApp() {
      this.$emit("toggleModal", {
        mode: "copy",
        app: this.app,
      });
    },
  },
  computed: {
    isDarkMode() {
      return this.$store.state.mode.darkMode;
    },
    theme() {
      return this.$vuetify.theme.dark ? "dark" : "light";
    },
    apps() {
      return this.$store.state.apps.apps;
    },
    isGlobalView() {
      return this.$route.query.c === "*";
    },
    runInCloudPortal() {
      return `${this.app.appID}-cloud`;
    },
    upgradeButtonPortal() {
      return `${this.app.appID}-upgrade`;
    },
    buildButtonPortal() {
      return `${this.app.appID}-build`;
    },
    showDownloadBinaryButton() {
      let visible = false;

      this.apps.forEach((app) => {
        for (let intf of app?.interfaces) {
          if (intf.enabled && intf.icon !== "server") {
            visible = true;
            return;
          }
        }
      });

      return visible;
    },
  },
};
</script>

<style lang="css">
.h2 > h2 {
  font-size: 18px;
}

.w-small > .v-input__control {
  max-height: 32px;
}

.w-small > .v-input__control > .v-input__slot {
  max-height: 32px;
}

.w-small > .v-input__control > .v-input__slot > .v-text-field__slot {
  max-height: 32px;
}

.w-small > .v-input__control > .v-input__slot > .v-text-field__slot > input {
  max-height: 32px;
  border: 1px solid #cccccc;
  margin-top: -16px;
  padding: 10px;
  border-radius: 3px;
}
</style>

<style lang="scss" scoped>
.w-small {
  width: 50px;
  margin: 10px;
  font-size: 13px;
}

.w-f {
  float: left;
}

.w-break {
  clear: both;
}

::v-deep .v-input__slot::before {
  border-style: none !important;
}

.build-app {
  background-color: white;
  margin: 24px 0;

  &__app-name {
    $text-field-outlined-fieldset-border-width: 0;
  }

  &__pointer {
    cursor: pointer;
  }

  &__save-cancel-name-edit {
    display: flex;
  }

  &__status-led {
    margin-right: 16px;
  }

  &--dark {
    background-color: var(--apio-shade-2);
    color: var(--apio-shade-4);
  }

  &__header-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    &--item {
      @media screen and (max-width: 767px) {
        margin-top: 8px;
      }
    }
  }

  &__rename-app-icon {
    cursor: pointer;
  }

  &__header {
    align-items: center;
    border-bottom: 1px solid var(--apio-shade-5);
    display: flex;
    height: 60px;
    justify-content: space-between;
    padding: 14px 16px;

    @media screen and (max-width: 1024px) {
      align-items: start;
      justify-items: center;
      flex-direction: column;
      height: auto;
    }

    &--title {
      display: flex;
      align-items: center;
      @media screen and (max-width: 1024px) {
        width: 100%;
        margin-bottom: 16px;
        justify-content: space-between;
      }
    }

    &--text {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      font-size: var(--font-h2-size);
      font-weight: var(--font-semi-bold);
      margin-right: 16px;

      @media screen and (max-width: 1024px) {
        font-size: var(--font-h3-size);
        font-weight: var(--font-semi-bold);
      }
    }

    &--icon {
      cursor: pointer;
    }
  }
}
</style>
